import { noteDetails } from "@/mocks/noteDetail";

export type NoteDoc = {
  id: string;
  title: string;
  subtitle: string;
  content: string;
  format?: "markdown" | "html";
  tags: string[];
  updatedAt: string; // yyyy-mm-dd
};

const SS_KEY = "zsk_notes_v1";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function loadMap(): Record<string, NoteDoc> {
  if (typeof window === "undefined") return {};
  return safeParse<Record<string, NoteDoc>>(window.sessionStorage.getItem(SS_KEY), {});
}

function saveMap(map: Record<string, NoteDoc>) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(SS_KEY, JSON.stringify(map));
}

export function upsertNoteDoc(doc: NoteDoc) {
  const map = loadMap();
  map[doc.id] = doc;
  saveMap(map);
}

export function listNoteDocs(): NoteDoc[] {
  const map = loadMap();
  return Object.values(map).sort((a, b) => (a.updatedAt === b.updatedAt ? b.id.localeCompare(a.id) : b.updatedAt.localeCompare(a.updatedAt)));
}

export function ensureSeedNotes(): void {
  const map = loadMap();
  if (Object.keys(map).length > 0) return;
  // 首次进入：把 mock noteDetails 种进去，确保列表可见
  const next: Record<string, NoteDoc> = {};
  for (const seed of noteDetails) {
    const content = seed.sections.map((s) => `## ${s.title}\n\n${s.content.join("\n\n")}`).join("\n\n");
    next[seed.id] = {
      id: seed.id,
      title: seed.title,
      subtitle: seed.subtitle,
      content,
      format: "markdown",
      tags: seed.tags,
      updatedAt: seed.date,
    };
  }
  saveMap(next);
}

export function getNoteDoc(id: string): NoteDoc | null {
  const map = loadMap();
  if (map[id]) return map[id]!;

  // fallback: from mock noteDetails (convert sections to one article)
  const seed = noteDetails.find((n) => n.id === id);
  if (!seed) return null;
  const content = seed.sections
    .map((s) => `## ${s.title}\n\n${s.content.join("\n\n")}`)
    .join("\n\n");
  return {
    id: seed.id,
    title: seed.title,
    subtitle: seed.subtitle,
    content,
    format: "markdown",
    tags: seed.tags,
    updatedAt: seed.date,
  };
}

export function createEmptyNoteDoc(id: string, title: string): NoteDoc {
  return {
    id,
    title,
    subtitle: "",
    content: "",
    format: "html",
    tags: [],
    updatedAt: today(),
  };
}

